import type { CompiledChapter } from "@/data/schema/compiled";
import { chapterNumber } from "../ChapterId";

export interface MarkerNames {
  displayName: string;
  /** The name changes from that chapter on. */
  laterNames?: readonly { chapterId: string; displayName: string; sceneId?: string }[];
  /** A name a later chapter reveals, shown earlier only with spoilers on. */
  revealedNames?: readonly { chapterId: string; name: string }[];
}

const numberOf = (chapterId: string | undefined): number =>
  chapterId === undefined ? 0 : chapterNumber(chapterId) ?? 0;

/** A name learned partway through the first chapter played starts as the earlier name. */
export function namesFromStart(character: {
  displayName: string;
  namedInScene?: string | undefined;
  earlierName?: string | undefined;
  laterNames?: MarkerNames["laterNames"] | undefined;
}): Pick<MarkerNames, "displayName" | "laterNames"> {
  const { displayName, namedInScene, earlierName, laterNames } = character;
  if (namedInScene === undefined || earlierName === undefined) {
    return { displayName, ...(laterNames === undefined ? {} : { laterNames }) };
  }
  const chapterId = namedInScene.split("-")[0] ?? namedInScene;
  return {
    displayName: earlierName,
    laterNames: [{ chapterId, displayName, sceneId: namedInScene }, ...(laterNames ?? [])],
  };
}

/** What the label under a portrait says during a chapter. */
export function markerNameAt(
  marker: MarkerNames,
  chapterId: string | undefined,
  spoilersOn: boolean,
  sceneReached: (sceneId: string) => boolean = () => true,
): string {
  const now = numberOf(chapterId);
  const name = (marker.laterNames ?? [])
    .filter((later) => numberOf(later.chapterId) < now || (
      numberOf(later.chapterId) === now
      && (later.sceneId === undefined || sceneReached(later.sceneId))
    ))
    .at(-1)?.displayName ?? marker.displayName;
  if (!spoilersOn) {
    return name;
  }
  const revealed = marker.revealedNames?.find((item) =>
    numberOf(item.chapterId) > now && item.name !== name);
  return revealed === undefined ? name : `${name} (${revealed.name})`;
}

/** Each character's revealed names, from the spoilers on the chapter's scenes. */
export function revealedNamesByCharacter(
  chapter: Pick<CompiledChapter, "scenes">,
): Map<string, { chapterId: string; name: string }[]> {
  const names = new Map<string, { chapterId: string; name: string }[]>();
  for (const scene of chapter.scenes) {
    for (const spoiler of scene.spoilers ?? []) {
      if (spoiler.characterId !== undefined && spoiler.revealedName !== undefined) {
        const list = names.get(spoiler.characterId) ?? [];
        if (!list.some((item) => item.name === spoiler.revealedName)) {
          list.push({ chapterId: spoiler.revealedInChapterId, name: spoiler.revealedName });
        }
        names.set(spoiler.characterId, list);
      }
    }
  }
  return names;
}
